import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Inject,
  ViewChild,
} from '@angular/core';
import { first } from 'rxjs/operators';
import { PopupService } from 'src/app/core/popup.service';
import { UserService } from 'src/app/core/user.service';
import { InstructionService } from 'src/app/core/instruction.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { environment } from 'src/environments/environment';
import { AccountSettingsService } from 'src/app/core/account-settings.service';
import { firstValueFrom } from 'rxjs';
import { UserFormComponent } from 'src/app/shared/user-form/user-form.component';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { LoadingIndicatorComponent } from 'src/app/shared/loading-indicator/loading-indicator.component';

/**
 * Component for all of the account settings.
 */
@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingIndicatorComponent,
    UserFormComponent,
  ],
})
export class AccountSettingsComponent implements AfterViewChecked {
  @ViewChild('userFormComponent') userFormComponent!: UserFormComponent;

  /** Settings form. */
  settingsForm: FormGroup;

  /** Whether the account settings is loading. */
  isLoading = false;

  // TODO: Re-enable when addressing notification settings
  // /** Notification settings model. */
  // notificationSettings: NotificationSettings;

  /** The version number for the app. */
  readonly appVersionNumber = environment.appVersionNumber;

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private popupService: PopupService,
    private accountSettingsService: AccountSettingsService,
    private instructionService: InstructionService,
    @Inject(ChangeDetectorRef) private changeDetectorR: ChangeDetectorRef,
  ) {
    this.settingsForm = this.fb.nonNullable.group({
      userForm: this.fb.nonNullable.control(''),
    });
  }

  /**
   * Updates all settings for the user.
   */
  updateSettings(): void {
    this.isLoading = true;
    this.updateUserAccount();
    // TODO: enable when notifications settings are addressed
    // this.updateNotificationSettings();
  }

  /** Discards any in-progress edits and returns to the read-only summary. */
  cancelEdit(): void {
    this.userFormComponent?.cancelEdit();
  }

  /**
   * Checks after the component views and child views.
   */
  ngAfterViewChecked(): void {
    this.changeDetectorR.detectChanges();
  }

  /**
   * Update user account settings data.
   */
  private updateUserAccount(): void {
    const userFormData = this.settingsForm.get('userForm')?.value;
    const { confirmPassword } = userFormData;
    const firstName = userFormData.firstName?.trim();
    const lastName = userFormData.lastName?.trim();
    const timeZone = userFormData.timeZone;
    const preferredLanguage = userFormData.preferredLanguage;
    const description = userFormData.description;
    this.userService
      .updateUserAccount({
        firstName,
        lastName,
        password: confirmPassword,
        timeZone: timeZone,
        description,
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.settingsForm.reset();
          this.userFormComponent?.isEditing.set(false);
          this.popupService.notify('Your account settings are updated.');
          this.accountSettingsService.setUser({
            firstName,
            lastName,
            timeZone: timeZone,
            profileImageRithmId:
              this.userService.user().profileImageRithmId || '',
          });
        },
        error: () => {
          this.isLoading = false;
          this.notify('Error updating account settings.', true);
        },
      });

    // A separate StationAPI call, not part of the UserService payload above (the instructions-language
    // preference lives in StationAPI's UserSetting table, not UserService) — fired in the same submit
    // handler so it only saves on "Save changes", never immediately on selection, unlike this same
    // preference's behavior in the runtime instructions panel.
    if (preferredLanguage) {
      this.instructionService
        .setPreferredLanguage(preferredLanguage)
        .pipe(first())
        .subscribe({
          error: () => {
            this.notify('Error updating your instructions language.', true);
          },
        });
    }
  }

  /**
   * Show toast message notify.
   * @param message Message.
   * @param isError Is error.
   */
  notify(message: string, isError = false): void {
    this.popupService.notify(message, isError);
  }

  /**
   * Open the terms and conditions modal.
   */
  async openTerms(): Promise<void> {
    let message = '';
    this.isLoading = true;
    try {
      const termsConditionsText = await firstValueFrom(
        this.userService.getTermsConditions(),
      );
      if (termsConditionsText) {
        message = termsConditionsText;
        this.isLoading = false;
        await this.popupService.terms({
          title: 'Terms and Conditions',
          message,
        });
      }
    } catch (error) {
      this.isLoading = false;
      this.notify(`Failed to open dialog`, true);
    }
  }
}
